'use client'

import { useCarouselScroll } from '@/hooks/useCarouselScroll'
import { ArrowButton } from '@/components/ui/arrow-button'
import type { AcademyCourseCardData } from '@/utilities/getAcademyHome'
import { AcademyCourseCard } from './AcademyCourseCard'

interface Props {
  courses: AcademyCourseCardData[]
  ariaLabel: string
}

/**
 * Tablet/mobile presentation of a difficulty group: a snap-scroll carousel with
 * a peek of the next card and dot pagination. Desktop uses a static grid instead.
 */
export function AcademyCourseCarousel({ courses, ariaLabel }: Props) {
  const { scrollRef, activeIndex, isAtStart, isAtEnd, scrollToIndex, handleKeyDown } =
    useCarouselScroll({
      itemCount: courses.length,
      autoplayDelay: 0,
    })

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={scrollRef}
        className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {courses.map((course) => (
          <div
            key={course.id}
            data-card
            className="w-[85%] shrink-0 snap-start md:w-[calc((100%-24px)/2)]"
          >
            <AcademyCourseCard course={course} />
          </div>
        ))}
      </div>

      {courses.length > 1 && (
        <div className="mt-5 flex items-center justify-end gap-2">
          <ArrowButton
            direction="left"
            backgroundColor="transparent"
            size="medium"
            variant="minimal"
            className="!border-black !text-black hover:!bg-black/5"
            onClick={() => scrollToIndex(activeIndex - 1)}
            disabled={isAtStart}
            aria-label="Previous course"
          />
          <ArrowButton
            direction="right"
            backgroundColor="transparent"
            size="medium"
            variant="minimal"
            className="!border-black !text-black hover:!bg-black/5"
            onClick={() => scrollToIndex(activeIndex + 1)}
            disabled={isAtEnd}
            aria-label="Next course"
          />
        </div>
      )}
    </div>
  )
}
